import { useState, useRef, useCallback } from 'react';
import { generateVoiceSample } from '../api/voiceSample';

export const useVoiceSample = (_environment?: 'staging' | 'production') => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);

  const cleanup = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  }, []);

  const playVoiceSample = async (voiceId: string, text?: string, language?: string) => {
    if (!voiceId) {
      console.error('Voice ID is required');
      return null;
    }

    cleanup();

    try {
      setIsGenerating(true);
      const blob = await generateVoiceSample(voiceId, text, language);
      setIsGenerating(false);

      const url = URL.createObjectURL(blob);
      urlRef.current = url;

      const audio = new Audio(url);
      audioRef.current = audio;

      setIsPlaying(true);
      audio.onended = () => {
        setIsPlaying(false);
        cleanup();
      };
      audio.onerror = () => {
        setIsPlaying(false);
        cleanup();
      };

      await audio.play();
      return audio;
    } catch (error) {
      console.error('Error playing voice sample:', error);
      setIsGenerating(false);
      setIsPlaying(false);
      cleanup();
      throw error;
    }
  };

  const stopPlayback = useCallback(() => {
    setIsPlaying(false);
    cleanup();
  }, [cleanup]);

  const downloadVoiceSample = async (
    voiceId: string,
    voiceName: string,
    text?: string,
    language?: string
  ) => {
    if (!voiceId) {
      console.error('Voice ID is required');
      return;
    }

    try {
      setIsDownloading(true);
      const blob = await generateVoiceSample(voiceId, text, language);

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${voiceName || 'voice_sample'}.wav`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading voice sample:', error);
      throw error;
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    playVoiceSample,
    stopPlayback,
    downloadVoiceSample,
    isGenerating,
    isPlaying,
    isDownloading,
  };
};
